import type { ReactNode } from 'react';

import { useNavigate, useRouteLoaderData } from '@remix-run/react';
import type { TNavbarItemElement } from '@shared/dream-components';
import { NavbarMenuItem as NavbarMenuItemElement } from '@shared/dream-components';

import { usePageContext } from '~/context/usePageContext';
import type { RootLoaderData } from '~/root';

import { getNodeActionAndVisibility } from '../utils/getNodeActionAndVisibility';

interface Props {
  node: TNavbarItemElement;
  children: ReactNode | ReactNode[];
  screen: 'desktop' | 'mobile';
}

const ClientNavbarMenuItem = ({ node, children, screen }: Props) => {
  const navigate = useNavigate();
  const { subscription } = usePageContext();
  const isPremiumUser = subscription?.tier === 'premium';
  const { isLoggedIn } = useRouteLoaderData<RootLoaderData>('root') || {};

  const onClick = node?.attrs && 'onClick' in node.attrs ? node.attrs.onClick : undefined;
  const action = node?.attrs && 'action' in node.attrs && node.attrs.action;
  const isOpenAction = onClick === 'action' || (onClick === undefined && !!action);

  const { handleClientSideNavigation } = getNodeActionAndVisibility(
    node,
    subscription,
    navigate,
    Boolean(isLoggedIn),
    isPremiumUser,
  );

  if (
    node.type === 'navbar_item' &&
    node.attrs &&
    isOpenAction &&
    isLoggedIn &&
    node.attrs.action === 'profile' &&
    node.attrs.type === 'user_button'
  ) {
    node.attrs.avatar = subscription?.avatar_url;
    node.attrs.firstName = subscription?.first_name;
    node.attrs.lastName = subscription?.last_name;
    node.attrs.email = subscription?.email;

    if (node.attrs.style === 'button') {
      node.attrs.href = undefined;
    } else {
      node.attrs.href = `/subscribe/${subscription?.subscriber_id}/manage`;
    }
  }

  return (
    <NavbarMenuItemElement
      element={node}
      controlbyselected="false"
      selected={false}
      screen={screen}
      onClick={handleClientSideNavigation}
    >
      {children}
    </NavbarMenuItemElement>
  );
};

const NavbarMenuItem = ({ node, children, screen }: Props) => {
  return (
    <ClientNavbarMenuItem node={node} screen={screen}>
      {children}
    </ClientNavbarMenuItem>
  );
};

export default NavbarMenuItem;
